import { useCallback, useEffect, useRef } from "react";
import { useLocation, useRouteMatch } from "react-router";

import { FlightPhaseEnum } from "@skydio/pbtypes/pbtypes/gen/common/flight_phase_enum_pb";

import { getFlightId, getFlightPhaseId } from "state/pilot/selectors";
import { getRoomName, getTeleopSessionId } from "state/teleop/selectors";
import { trackAnalytic, trackUserAction } from "utils/trackEvent";

import { useSelector } from "./redux";
import { useFeatureFlag } from "./useFeatureFlag";

type TrackUserAction = (name: string, payload?: object) => void;
type TrackAnalytic = (name: string, payload?: object) => void;

/**
 * Track a streaming event in mixpanel, with common properties like session ID, flight ID, and
 * vehicle ID. The returned callback will not change between renders.
 */
export function useTrackStreamingEvent(): [TrackUserAction, TrackAnalytic] {
  const common = useRef<{
    teleopSessionId?: string;
    roomName?: string;
    flightId?: string;
    vehicleId?: string;
    flightPhase?: string;
    surface?: string;
  }>({});

  const teleopSessionId = useSelector(getTeleopSessionId);
  const roomName = useSelector(getRoomName);
  const flightId = useSelector(getFlightId);
  const flightPhase = useSelector(getFlightPhaseId);
  const location = useLocation();
  const {
    params: { vehicleId },
  } = useRouteMatch<{ vehicleId: string }>();

  // This ff was made for RUM events, but it's being used for all DD events
  const enableStreamingDDEvents = useFeatureFlag("enableStreamingRumEvents");

  /**
   * NOTE(trey): the implementation of this hook is admittedly odd. this approach ensures the track
   * callback will not change between renders. we could bake the common parameters into the returned
   * callback, but this would trigger updates much more frequently. given the current setup of LiveKit
   * components (namely LivekitSkybusProvider and setupLivekitSkybus), we need to minimize updates to
   * avoid turning over the WebRTC connection. once these components have been refactored, we can
   * probably simplify this to a simple hook with no shenanigans. one potential downside of the
   * current approach is that we could log an event with stale parameters, but this isn't a big deal
   * in this case because all the common parameters change very infrequently.
   */
  useEffect(() => {
    // Determine surface based on pathname
    let surface: string | undefined;
    const { pathname } = location;

    if (pathname.includes("/fleet/flights")) {
      surface = "fleetvehicle";
    } else if (pathname.includes("/flight/live/")) {
      surface = "teleop";
    } else if (pathname.includes("/flight/vehicle-stream/")) {
      surface = "livestream";
    } else if (pathname.startsWith("/shared_link/")) {
      surface = "readylink";
    }

    common.current.teleopSessionId = teleopSessionId;
    common.current.roomName = roomName;
    common.current.surface = surface;
    common.current.flightId = flightId;
    common.current.vehicleId = vehicleId;
    common.current.flightPhase = Object.entries(FlightPhaseEnum.Enum).find(
      ([, v]) => v === flightPhase
    )?.[0];
  }, [teleopSessionId, roomName, flightId, flightPhase, vehicleId, location]);

  const trackUserActionEvent = useCallback(
    (name: string, payload?: object) => {
      return trackUserAction(name, { ...common.current, ...payload }, enableStreamingDDEvents);
    },
    [enableStreamingDDEvents]
  );

  const trackAnalyticEvent = useCallback(
    (name: string, payload?: object) =>
      trackAnalytic(name, { ...common.current, ...payload }, enableStreamingDDEvents),
    [enableStreamingDDEvents]
  );

  return [trackUserActionEvent, trackAnalyticEvent];
}
